import React, { useState, useEffect } from 'react'
import { Row, Col } from 'react-bootstrap'
import axios from 'utility/axios'
import { useFormik } from 'formik'
import { useRouter } from 'next/router'
import Link from 'components/common/Link'
import * as propertyQuery from 'utility/queries/property'
import Stripe from 'assets/inline/Stripe.png'
import { formatMoneyCents, formatMoneyCentsWithDecimals } from 'utility/numbers'
import { PlanSelectionBlock } from 'components/common/blocks/PlanSelectionBlock'
import { usePlans } from 'hooks/api/usePlans'
import * as google from 'utility/google'

import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'

import * as Yup from 'yup'
import { useTenantProfile } from 'hooks/api/tenant/useTenantProfile'
import FormInput from 'components/common/forms/elements/FormInput'
import PropertyForm from 'components/common/forms/PropertyForm'
import Card from 'components/common/cards/Card'
import Text from 'components/common/Text'
import Button from 'components/common/Button'
import FormLabel from 'components/common/forms/elements/FormLabel'
import { useApp } from 'components/providers/AppProvider'
import { useSelf } from 'components/providers/SelfProvider'
import getNested from 'utility/getNested'
import { formatPhoneNumber } from 'utility/numbers'
import colors from 'utility/colors'
import Spinner from 'components/common/Spinner'
import { useUser, useUserNotifications } from 'hooks/api/user/useUser'
import { toast } from 'react-toastify'
import { useSetupIntentClientSecret } from 'hooks/api/useSetupIntentClientSecret'
import { useTenantSettings } from 'hooks/api/tenant/useTenantSettings'
import Script from 'next/script'

import { POST_CREATE_GROUP, POST_ADD_PROPERTIES } from 'utility/endpoints'
import * as logrocket from 'utility/logrocket'
import * as crisp from 'utility/crisp'

import logger from 'utility/logger'
import { StripeWrapper } from '../StripeWrapper'

const labelColorStyle = {
	color: colors.GRAY,
	fontWeight: 'bold',
	fontSize: '14px',
	marginBottom: 7,
}

const formSpacing = {
	marginBottom: 20,
}

const SignupBlock = ({
	agentId,
	propertyArray = [],
	onUpdateProperties = (properties) => console.log('Properties updated', properties),
	light = false,
	primaryColor = colors.PRIMARY,
	group,
	promptText,
}) => {
	const router = useRouter()
	const { data: tenantProfile } = useTenantProfile()
	const tenantName = tenantProfile?.name

	const { slug, userSlug, plan } = router.query
	const { refetch: refetchUser, data: user } = useUser(userSlug)
	const { refetch: refetchNotifications } = useUserNotifications(userSlug)
	const { mutateAsync: fetchClientSecret } = useSetupIntentClientSecret()
	const { fetchSelfFromAuthToken } = useSelf()
	const { setAuthToken } = useApp()

	const isSignup = !group
	const [properties, setProperties] = useState(
		propertyArray
			? propertyArray?.map((prop) => {
					return {
						...prop,
						added: new Date(),
					}
				})
			: [],
	)

	useEffect(() => {
		if (!propertyArray.length) return

		setProperties(
			propertyArray?.map((prop) => {
				return {
					...prop,
					added: new Date(),
				}
			}),
		)
	}, [propertyArray])

	const { data: plans } = usePlans({})
	const [selectedPlan, setSelectedPlan] = useState()

	const [error, setError] = useState()
	const [success, setSuccess] = useState()
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		if (!selectedPlan) {
			const foundPlan =
				plans?.availablePlans?.find((p) => p.planId === plan) ||
				plans?.alternatePlans?.find((p) => p.planId === plan)
			setSelectedPlan(foundPlan || plans?.currentPlan || plans?.availablePlans?.[0])
		}
	}, [plans, setSelectedPlan, plan])

	const stripe = useStripe()
	const elements = useElements()
	const skipDeadlineCheck = getNested(router, 'query', 'skipDeadlineCheck') === 'true'

	const textColor = light ? colors.WHITE : colors.PRIMARY

	const avaliableProperties = properties?.filter((prop) => {
		if (skipDeadlineCheck) {
			return !getNested(prop, 'meta', 'signupState', 'isProtected') ? true : false
		} else {
			return getNested(prop, 'meta', 'signupState', 'canSignup') ? true : false
		}
	})

	const firstName = getNested(group, 'groupName')?.split(' ')[0] || ''
	const lastName = getNested(group, 'groupName')?.split(' ')[1] || ''

	const nameBlacklist = ['LLC', 'Trust', 'Inc', '&', 'And', 'Properties']

	const calcNumberOfProperties = avaliableProperties?.length || 1
	const baseInDollars = selectedPlan?.membershipFeeAmountInCents * 0.01
	const finalTotalWithoutDiscount = baseInDollars * calcNumberOfProperties

	const finalTotalWithDiscount =
		selectedPlan?.partnerDiscountPct && selectedPlan?.canApplyPartnerDiscount
			? finalTotalWithoutDiscount -
				finalTotalWithoutDiscount * (selectedPlan?.partnerDiscountPct / 100)
			: finalTotalWithoutDiscount

	let discountTitle = selectedPlan?.partnerDiscountDesc || '1st year discount'

	const formik = useFormik({
		// Add first name and last name from group here
		initialValues: {
			firstName,
			lastName,
			email: '',
			phoneNumber: '',
			tos: false,
		},
		onSubmit: async (values, { resetForm }) => {
			if (loading) return

			// clear the errors and set loading to true
			setError(null)
			setLoading(true)
			// setGlobalLoading(true) ==> Todo - add this back when craig is ready

			if (!stripe || !elements) return
			elements.submit()

			const { firstName, lastName, email, phoneNumber } = values
			const userFullName = `${firstName && firstName.trim()} ${lastName && lastName.trim()}`
			let number = phoneNumber && phoneNumber.replace(/[^0-9]/g, '')

			// This needs to get reformatted into a single unified area

			if (isSignup) {
				logrocket.identify(email, {
					name: userFullName,
					email: email,
					phoneNumber: number,
					agentId: agentId ? agentId : '',
					agent: false,
				})
				let logrocketUrl = logrocket.getSessionURL()
				crisp.identify('email', email)
				crisp.identify('phone', number)
				crisp.identify('nickname', userFullName)
				crisp.customData('agentId', agentId)
				crisp.customData('isHomeowner', true)
				crisp.customData('logrocketUrl', logrocketUrl)
			}

			try {
				const clientSecret = await fetchClientSecret()

				const billingDetails = {
					name: [firstName, lastName].filter(Boolean).join(' ') || '',
					email: email || '',
					phone: phoneNumber || '',
				}

				const { setupIntent, error: setupIntentError } = await stripe.confirmSetup({
					elements,
					clientSecret,
					confirmParams: {
						payment_method_data: {
							billing_details: billingDetails,
						},
					},
					redirect: 'if_required',
				})

				if (!avaliableProperties || !avaliableProperties.length) {
					setLoading(false)
					// setGlobalLoading(false)
					logger.default(
						'Please search and select at least one property that is not already signed up, or past a deadline.',
					)
					return setError(
						'Please search and select at least one property that is not already signed up, or past a deadline.',
					)
				}

				if (!selectedPlan) {
					setLoading(false)
					logger.default('Please select a plan to continue.')
					return setError('Please select a plan to continue.')
				}

				if (!setupIntent) {
					setLoading(false)
					logger.default('There was an issue with your payment method.')
					return setError('There was an issue with your payment method.')
				}

				let data
				if (isSignup) {
					data = {
						firstName,
						lastName,
						emailAddress: email,
						homeownerPlanId: selectedPlan?.planId,
						isPropertyManager: false, // use to have a value here, deprecated
						membershipFeeTotalAmountInCents: finalTotalWithDiscount * 100,
						partnerId: agentId ? agentId : '',
						paymentMethodId: setupIntent.payment_method,
						propertyRequests: avaliableProperties.map((property, index) => {
							let propertyId = getNested(property, 'id')
							let propertyParts = propertyId && propertyId.split('-')
							let countyId = +propertyParts[0]
							let countyNumber = propertyParts[1]
							return {
								countyId,
								countyName: getNested(property, 'htsGroup', 'county', 'name'),
								countyNumber,
								isPrimary: index === 0 ? true : false,
								ownerName: getNested(property, 'htsGroup', 'owner', 'name'),
								ownerAddress: getNested(property, 'htsGroup', 'owner', 'address'),
								ownerCity: getNested(property, 'htsGroup', 'owner', 'city'),
								ownerState: getNested(property, 'htsGroup', 'owner', 'state'),
								ownerZip: getNested(property, 'htsGroup', 'owner', 'zip'),
								propertyAddress: getNested(property, 'htsGroup', 'address'),
							}
						}),
						telephone: number,
					}
				} else {
					data = {
						groupSlugId: slug,
						homeownerPlanId: selectedPlan?.planId,
						membershipFeeTotalAmountInCents: (finalTotalWithDiscount || 0) * 100,
						paymentMethodId: setupIntent.payment_method,
						propertyRequests: avaliableProperties.map((property, index) => {
							let propertyId = getNested(property, 'id')
							let propertyParts = propertyId && propertyId.split('-')
							let countyId = +propertyParts[0]
							let countyNumber = propertyParts[1]
							return {
								countyId,
								countyName: getNested(property, 'htsGroup', 'county', 'name'),
								countyNumber,
								isPrimary: index === 0 ? true : false,
								ownerName: getNested(property, 'htsGroup', 'owner', 'name'),
								ownerAddress: getNested(property, 'htsGroup', 'owner', 'address'),
								ownerCity: getNested(property, 'htsGroup', 'owner', 'city'),
								ownerState: getNested(property, 'htsGroup', 'owner', 'state'),
								ownerZip: getNested(property, 'htsGroup', 'owner', 'zip'),
								propertyAddress: getNested(property, 'htsGroup', 'address'),
							}
						}),
					}
				}

				let response
				if (isSignup) {
					response = await axios.post(POST_CREATE_GROUP, data, {
						timeout: 3600000,
					})
					console.log('AuthToken: ', response?.data?.authToken)
					if (response?.data?.authToken) {
						setAuthToken(response?.data?.authToken)
						fetchSelfFromAuthToken(response?.data?.authToken)
					}
				} else {
					response = await axios.post(POST_ADD_PROPERTIES, data, {
						timeout: 3600000,
					})
					refetchUser()
				}
				console.log('Signup Token Complete: ', localStorage.getItem('htsauthToken'))

				setLoading(false)
				if (response?.status === 200) {
					if (isSignup) {
						crisp.customData('signedUp', true)
						crisp.customData('signedUpOn', new Date().toString())
						crisp.customData('isHomeowner', true)

						crisp.customEvent('Signup Completed Homeowner Registration')
						logger.default('Signup completed successfaully, routing to success page...')

						google.event({
							action: 'form',
							category: 'conversion',
							label: 'homeowner-success',
							value: `${email} Signed Up as a Homeowner${agentId ? ` via Agent ${agentId}` : ''}`,
						})

						google.event({
							action: 'agentId',
							agentId,
						})

						google.event({
							action: agentId ? 'homeowner_partner_new' : 'homeowner_direct_new',
							category: 'conversion',
							number_homes: data.propertyRequests.length,
						})
					} else {
						google.event({
							action: agentId ? 'homeowner_partner_add' : 'homeowner_direct_add',
							category: 'conversion',
							number_homes: data.propertyRequests.length,
						})
					}

					elements.getElement(PaymentElement).clear()
					setSuccess(true)
					resetForm()

					if (isSignup) {
						toast.success(`Welcome to ${tenantName}!`)
						const userSlug = response?.data?.userSlugId
						if (!userSlug) {
							throw new Error('User slug not found in response.')
						}

						router.push({
							pathname: '/users/[userSlug]/properties',
							query: {
								userSlug,
								welcome: true,
							},
						})
					} else {
						refetchNotifications()

						let joinedProperties = avaliableProperties
							.map((property) => getNested(property, 'id'))
							.join(',')
						const year = avaliableProperties[0]?.htsGroup?.taxYear
						toast.success('Your properties have been added!')
						router.push({
							// pathname: '/users/[userSlug]/groups/[slug]/properties',
							pathname: '/users/[userSlug]/properties',
							query: {
								userSlug,
								// slug,
								year,
								new: joinedProperties,
							},
						})
					}
				}
			} catch (err) {
				setLoading(false)
				// setGlobalLoading(false)
				logger.error('Signup Error Homeowner Registration', err)
				if (err.response) {
					let response = getNested(err, 'response')
					let status = getNested(response, 'status')
					let message = getNested(response, 'data')

					if (status === 402) return setError('Sorry, there was an issue with your payment method.')

					if (status === 400 && message) {
						let finalMessages = []
						const emailInUse = message.includes('Email Address is already used.')
						const phoneInUse = message.includes('Phone Number is already used.')

						if (emailInUse || phoneInUse) {
							finalMessages.push(
								<>
									This {phoneInUse ? 'phone number' : emailInUse ? 'email' : 'account'} is already
									associated with an account. Please{' '}
									<Link href={`/auth/signin`} target='_blank'>
										CLICK HERE to login to your account.
									</Link>
								</>,
							)
						} else {
							finalMessages.push(message)
						}
						// finalMessages.push('Please contact us to add additional properties to your account.')

						if (finalMessages && finalMessages.length) {
							return setError(finalMessages)
						} else {
							return setError(message)
						}
					}

					if (status === 500) {
						if (!message) {
							return setError(
								`Sorry, there's an unknown system error. Please ensure you've filled out all required fields and try again. Sometimes trying a different browser or payment method helps. If it still doesn't work, please reach out to our support team via the webchat feature on our website. (500)`,
							)
						}

						return setError(message)
					}
				} else {
					setError(
						`Sorry, there's an unknown system error. Please ensure you've filled out all required fields and try again. Sometimes trying a different browser or payment method helps. If it still doesn't work, please reach out to our support team via the webchat feature on our website.`,
					)
				}
			}
		},
		validationSchema: Yup.object({
			firstName: isSignup
				? Yup.string()
						.max(30, 'Must be 30 characters or less')
						.test(
							'is-not-blacklist',
							'First + last name must be one person, not multiple people or an entity.',
							(value) => {
								if (value) {
									const firstNamePieces = value.toLowerCase().split(' ')
									const blackListExistsInName = nameBlacklist.filter((blackListedName) => {
										return firstNamePieces.includes(blackListedName.toLowerCase())
									})
									return !blackListExistsInName.length
								}
							},
						)
						.required('Required')
				: null,
			lastName: isSignup
				? Yup.string()
						.max(30, 'Must be 30 characters or less')
						.test(
							'is-not-blacklist',
							'First + last name must be one person, not multiple people or an entity.',
							(value) => {
								if (value) {
									const lastNamePieces = value.toLowerCase().split(' ')
									const blackListExistsInName = nameBlacklist.filter((blackListedName) => {
										return lastNamePieces.includes(blackListedName.toLowerCase())
									})

									return !blackListExistsInName.length
								}
							},
						)
						.required('Required')
				: null,
			email: isSignup ? Yup.string().email('Invalid email address').required('Required') : null,
			phoneNumber: isSignup
				? Yup.string()
						.min(14, 'Please enter a 10 digit cell phone number')
						.max(14, 'Please enter a 10 digit cell phone number')
						.required('Required')
				: null,
			tos: Yup.bool().oneOf([true], 'Required'),
			confirmation: Yup.bool().oneOf([true], 'Required'),
		}),
		validateOnChange: true,
		validateOnBlur: true,
	})

	const errorStyle = {
		color: colors.RED,
	}

	const overFlowTextStyle = {
		overflow: 'initial',
		textOverflow: 'unset',
	}

	return (
		<>
			{success ? (
				<Script async src='https://tag.simpli.fi/sifitag/bfba3b90-9338-013a-9f25-06b4c2516bae' />
			) : null}

			<form onSubmit={formik.handleSubmit}>
				{isSignup && (
					<Card noShadow background={colors.MUTED_GRAY}>
						<Row>
							<Col xs='12' md='12' lg='9' xl='8'>
								<FormInput
									labelStyle={labelColorStyle}
									formContainerStyle={formSpacing}
									label={'FIRST NAME'}
									placeholder={'John'}
									type='text'
									id='firstName'
									name='firstName'
									onBlur={formik.handleBlur}
									onChange={formik.handleChange}
									value={formik.values.firstName}
									required
									errorStyle={errorStyle}
									error={formik.touched.firstName && formik.errors.firstName}
								/>
							</Col>
							<Col xs='12' md='12' lg='9' xl='8'>
								<FormInput
									labelStyle={labelColorStyle}
									formContainerStyle={formSpacing}
									label={'LAST NAME'}
									placeholder={'Appleseed'}
									type='text'
									id='lastName'
									name='lastName'
									onBlur={formik.handleBlur}
									onChange={formik.handleChange}
									value={formik.values.lastName}
									required
									errorStyle={errorStyle}
									error={formik.touched.lastName && formik.errors.lastName}
								/>
							</Col>
							<Col xs='12' md='12' lg='9' xl='8'>
								<FormInput
									labelStyle={labelColorStyle}
									formContainerStyle={formSpacing}
									label={'EMAIL'}
									placeholder={'hello@example.com'}
									type='text'
									id='email'
									name='email'
									onBlur={formik.handleBlur}
									onChange={formik.handleChange}
									value={formik.values.email}
									required
									errorStyle={errorStyle}
									error={formik.touched.email && formik.errors.email}
								/>
							</Col>
							<Col xs='12' md='12' lg='9' xl='8'>
								<FormInput
									labelStyle={labelColorStyle}
									formContainerStyle={formSpacing}
									placeholder={'(123) 456-7890'}
									label={'CELL PHONE'}
									type='phone'
									id='phoneNumber'
									name='phoneNumber'
									onBlur={formik.handleBlur}
									onChange={(e) => {
										const value = e.target.value
										const formattedPhoneNumber = formatPhoneNumber(value)
										const finalPhoneNumber = formattedPhoneNumber ? formattedPhoneNumber : value
										formik.setFieldValue('phoneNumber', finalPhoneNumber)
									}}
									value={formik.values.phoneNumber}
									required
									errorStyle={errorStyle}
									error={formik.touched.phoneNumber && formik.errors.phoneNumber}
								/>
							</Col>
						</Row>
					</Card>
				)}
				<Card noShadow background={colors.MUTED_GRAY} padding={20}>
					<Row style={{ marginBottom: 10 }}>
						<Col xs='12'>
							<PropertyForm
								promptText={promptText}
								labelColor={textColor}
								hideDeadlineBadges={skipDeadlineCheck}
								primaryColor={primaryColor}
								labels={
									<div>
										<Text
											size={14}
											color={colors.GRAY}
											style={{ marginTop: 8, fontWeight: 'bold', display: 'inline' }}
										>
											PROPERTY SEARCH
										</Text>
										<Link to='/coverage' target={'_blank'} style={{ display: 'inline' }}>
											<Text
												size={14}
												color={colors.GRAY}
												style={{
													display: 'inline',
													marginTop: 8,
													marginLeft: 8,
													textAlign: 'left',
												}}
											>
												Serving{' '}
												<Text
													style={{
														display: 'inline',
														fontStyle: 'italic',
														fontSize: 14,
														color: primaryColor,
													}}
												>
													these Texas counties
												</Text>
											</Text>
										</Link>
									</div>
								}
								inputUser={{
									firstName: formik.values.firstName,
									lastName: formik.values.lastName,
								}}
								selectedProperties={properties}
								updateProperty={(updatedProperty) => {
									const selectedProperties = properties.map((_property) => {
										if (updatedProperty.id === _property.id) return updatedProperty
										return _property
									})

									setProperties([...selectedProperties])
								}}
								onRemove={(property) => {
									const finalProperties = [
										...properties.filter((_property) => _property.id !== property.id),
									]
									setProperties(finalProperties)
									onUpdateProperties(finalProperties)
								}}
								onAdd={async (property) => {
									const foundExistingProperty = properties.filter(
										(_property) =>
											property.searchId === _property.searchId ||
											property.searchId === _property.id,
									)
									if (foundExistingProperty && foundExistingProperty.length) return
									// Get the 'Searched Property' and fetch the full property with all the details
									const propDetails = await propertyQuery.fetchPropertyById({
										id: getNested(property, 'searchId'),
									})

									const fullProperty = {
										...propDetails,
										added: new Date(),
									}
									setProperties([fullProperty, ...properties])
									onUpdateProperties([fullProperty, ...properties])
								}}
							/>
						</Col>
					</Row>
				</Card>

				{!plans?.currentPlan ? (
					<PlanSelectionBlock
						title='Select Plan'
						backgroundColor={colors.MUTED_GRAY}
						planId={plan}
						highlightedPlanId={selectedPlan?.planId}
						handlePlanChange={(plan) => {
							setSelectedPlan(plan)
						}}
					/>
				) : null}

				<Row style={{ marginBottom: 10 }}>
					<Col xs='12' lg='12'>
						<Card
							title='Payment Info'
							style={{ padding: 20 }}
							noShadow
							background={colors.MUTED_GRAY}
						>
							{/* AMOUNT DUE BLOCK */}
							<div>
								<Row>
									<Col xs={'9'} md={'8'} lg={'9'}>
										<Text color={textColor} align={'left'}>
											Per Property Fee
										</Text>
										<Text color={textColor} align={'left'}>
											Number of Properties
										</Text>
										{selectedPlan?.partnerDiscountPct && selectedPlan?.canApplyPartnerDiscount ? (
											<Text align={'left'} bold color={primaryColor}>
												{discountTitle}
											</Text>
										) : null}
									</Col>
									<Col xs={'3'} md={'4'} lg={'3'}>
										<Text color={textColor} align={'left'} style={overFlowTextStyle}>
											${formatMoneyCents(baseInDollars * 100, 0)}
										</Text>
										<Text color={textColor} align={'left'} style={overFlowTextStyle}>
											{calcNumberOfProperties}
										</Text>
										{selectedPlan?.partnerDiscountPct && selectedPlan?.canApplyPartnerDiscount ? (
											<Text align={'left'} style={overFlowTextStyle} bold color={primaryColor}>
												-{`${selectedPlan?.partnerDiscountPct}%`}
											</Text>
										) : null}
									</Col>
								</Row>
								<hr style={{ backgroundColor: textColor }} />
								<Row>
									<Col xs={'9'} md={'8'} lg={'9'}>
										<Text color={textColor} align={'left'}>
											Due Today
										</Text>
									</Col>
									<Col xs={'3'} md={'4'} lg={'3'}>
										<Text color={textColor} align={'left'} style={overFlowTextStyle} bold>
											${formatMoneyCentsWithDecimals(finalTotalWithDiscount * 100, 2) || 0}
										</Text>
									</Col>
								</Row>
							</div>

							<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'space-between',
									marginTop: 30,
								}}
							>
								<FormLabel style={labelColorStyle} required>
									BILLING INFO
								</FormLabel>
								<img
									alt={'Protected by Stripe'}
									src={Stripe}
									style={{ width: 75, height: 17, marginBottom: 3 }}
								/>
							</div>
							<PaymentElement
								id='payment-element'
								options={{
									fields: {
										billingDetails: {
											name: 'never',
											email: 'never',
											phone: 'never',
											address: 'auto',
										},
									},
								}}
							/>
						</Card>
					</Col>
				</Row>

				<Row style={{ marginBottom: 0 }}>
					<Col xs='12'>
						<FormInput
							label={
								<Text color={textColor}>
									I understand (1) {tenantName} will file my protest(s) and that I haven't/won't
									file my own protest, (2) savings are never guaranteed but, depending on the plan I
									selected, I may owe a success fee on tax savings, and (3) my properties will be
									auto-renewed each year until I take action to cancel my properties on my{' '}
									{tenantName} account online.
								</Text>
							}
							formContainerStyle={{
								display: 'flex',
								flexDirection: 'row',
								gap: 10,
							}}
							type='checkbox'
							id='confirmation'
							name='confirmation'
							onBlur={formik.handleBlur}
							style={{ marginBottom: 0 }}
							onChange={formik.handleChange}
							value={formik.values.confirmation}
							required
							errorStyle={errorStyle}
							error={formik.touched.confirmation && formik.errors.confirmation}
						/>
					</Col>
				</Row>

				<Row>
					<Col xs='12'>
						<FormInput
							label={
								<Text color={textColor}>
									I acknowledge {tenantName} will assign a licensed professional to represent me. I
									agree to the{' '}
									<Link
										target='_blank'
										href='/terms-of-service'
										style={{ color: primaryColor, display: 'inline' }}
									>
										Terms of Service
									</Link>{' '}
									and{' '}
									<Link
										target='_blank'
										href='/privacy-policy'
										style={{ color: primaryColor, display: 'inline' }}
									>
										Privacy Policy.
									</Link>{' '}
									and understand I can opt-out of SMS by responding STOP at any time.
								</Text>
							}
							formContainerStyle={{
								display: 'flex',
								flexDirection: 'row',
								gap: 10,
							}}
							type='checkbox'
							id='tos'
							name='tos'
							onBlur={formik.handleBlur}
							style={{ marginBottom: 0 }}
							onChange={formik.handleChange}
							value={formik.values.tos}
							required
							errorStyle={errorStyle}
							error={formik.touched.tos && formik.errors.tos}
						/>
					</Col>
				</Row>

				{error && (
					<Row>
						<Col xs='12'>
							<Card style={{ padding: 10 }}>
								<Text color={'red'} align='center' style={{ padding: 0, margin: 0 }}>
									{error}
								</Text>
							</Card>
						</Col>
					</Row>
				)}

				<Row>
					<Col xs='12'>
						<Button
							style={{
								width: 'auto',
								paddingLeft: 30,
								paddingRight: 30,
								marginTop: 10,
								align: 'left',
							}}
							id='submit-pay-authorize'
							type='Submit'
							disabled={loading || !formik.isValid}
							background={primaryColor}
						>
							{loading ? (
								<>
									<Text style={{ paddingBottom: 0, marginBottom: 0 }} color={colors.WHITE}>
										Submitting...{' '}
										<Spinner size={15} style={{ paddingLeft: 10 }} color={colors.WHITE} />
									</Text>{' '}
								</>
							) : (
								'PAY & AUTHORIZE'
							)}
						</Button>
					</Col>
				</Row>
			</form>
		</>
	)
}

const FullSignupBlock = (props) => {
	return (
		<StripeWrapper>
			<SignupBlock {...props} />
		</StripeWrapper>
	)
}

export default FullSignupBlock
